import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Pencil, Search, X, ChevronLeft, ChevronRight, MoreVertical } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { feesMasterApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { FeesMasterListItem, CreateFeesMasterRequest, UpdateFeesMasterRequest, FeesType } from '@/types'
import { usePermissions } from '@/contexts/PermissionContext'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]
const ALL_STATUSES = '__all__'
const ALL_TYPES = '__all__'

interface FeesFilters {
  searchTerm: string
  status: string
  type: string
}

export default function FeesMasterPage() {
  const { t } = useTranslation()
  const { canAddEdit } = usePermissions()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingFees, setEditingFees] = useState<FeesMasterListItem | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [filters, setFilters] = useState<FeesFilters>({
    searchTerm: '',
    status: ALL_STATUSES,
    type: ALL_TYPES,
  })
  const [formData, setFormData] = useState<CreateFeesMasterRequest>({
    fees_name: '',
    fees_code: '',
    sac_code: '',
    fees_type: 'PERCENTAGE',
    fees_percentage: 0,
    fees_amount: 0,
  })

  const queryClient = useQueryClient()

  const { data: feesList = [], isLoading } = useQuery({
    queryKey: ['fees'],
    queryFn: () => feesMasterApi.getAll(),
  })

  // Filter fees based on filters
  const filteredFees = useMemo(() => {
    return feesList.filter(fees => {
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase()
        const matchesName = fees.fees_name.toLowerCase().includes(searchLower)
        const matchesCode = fees.fees_code.toLowerCase().includes(searchLower)
        if (!matchesName && !matchesCode) return false
      }
      if (filters.status !== ALL_STATUSES) {
        const isActive = filters.status === 'active'
        if (fees.is_active !== isActive) return false
      }
      if (filters.type !== ALL_TYPES) {
        if (fees.fees_type !== filters.type) return false
      }
      return true
    })
  }, [feesList, filters])

  // Pagination calculations
  const totalRecords = filteredFees.length
  const totalPages = Math.ceil(totalRecords / pageSize)
  const paginatedFees = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    return filteredFees.slice(startIndex, startIndex + pageSize)
  }, [filteredFees, currentPage, pageSize])

  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const endRecord = Math.min(currentPage * pageSize, totalRecords)

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setCurrentPage(1)
  }

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1)
  }

  const goToPreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1)
  }

  const clearFilters = () => {
    setFilters({ searchTerm: '', status: ALL_STATUSES, type: ALL_TYPES })
    setCurrentPage(1)
  }

  const hasActiveFilters = filters.searchTerm || filters.status !== ALL_STATUSES || filters.type !== ALL_TYPES

  const createMutation = useMutation({
    mutationFn: feesMasterApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fees'] })
      setIsDialogOpen(false)
      resetForm()
      toast.success(t('masters.fees.feesCreated'))
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('masters.fees.failedToCreate')
      toast.error(errorMessage)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFeesMasterRequest }) =>
      feesMasterApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fees'] })
      setIsDialogOpen(false)
      setEditingFees(null)
      resetForm()
      toast.success(t('masters.fees.feesUpdated'))
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('masters.fees.failedToUpdate')
      toast.error(errorMessage)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: feesMasterApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fees'] })
      toast.success(t('masters.fees.feesDeleted'))
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('masters.fees.failedToDelete')
      toast.error(errorMessage)
    },
  })

  const resetForm = () => {
    setFormData({
      fees_name: '',
      fees_code: '',
      sac_code: '',
      fees_type: 'PERCENTAGE',
      fees_percentage: 0,
      fees_amount: 0,
    })
    setEditingFees(null)
  }

  const handleEdit = (fees: FeesMasterListItem) => {
    setEditingFees(fees)
    setFormData({
      fees_name: fees.fees_name,
      fees_code: fees.fees_code,
      sac_code: fees.sac_code || '',
      fees_type: fees.fees_type,
      fees_percentage: fees.fees_percentage,
      fees_amount: fees.fees_amount,
    })
    setIsDialogOpen(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (editingFees) {
      updateMutation.mutate({
        id: editingFees.fees_id,
        data: formData,
      })
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleOpenChange = (open: boolean) => {
    setIsDialogOpen(open)
    if (!open) {
      resetForm()
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t('masters.fees.title')}</h1>
          <p className="text-muted-foreground">
            {t('masters.fees.subtitle')}
          </p>
        </div>

        {canAddEdit('FEES_MASTER') && (
          <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                {t('masters.fees.addFees')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
            <form onSubmit={handleSubmit}>
              <DialogHeader>
                <DialogTitle>{editingFees ? t('masters.fees.editFees') : t('masters.fees.addFees')}</DialogTitle>
                <DialogDescription>
                  {editingFees ? t('masters.fees.updateFeesConfig') : t('masters.fees.createNewFeesConfig')}
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="fees_name">{t('masters.fees.feesName')} *</Label>
                  <Input
                    id="fees_name"
                    value={formData.fees_name}
                    onChange={(e) => setFormData({ ...formData, fees_name: e.target.value })}
                    placeholder="e.g., Service Charge, Processing Fee"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="fees_code">{t('masters.fees.feesCode')} *</Label>
                    <Input
                      id="fees_code"
                      value={formData.fees_code}
                      onChange={(e) => setFormData({ ...formData, fees_code: e.target.value })}
                      placeholder="e.g., SVC001"
                      required
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="sac_code">{t('masters.fees.sacCodeOptional')}</Label>
                    <Input
                      id="sac_code"
                      value={formData.sac_code || ''}
                      onChange={(e) => setFormData({ ...formData, sac_code: e.target.value })}
                      placeholder="e.g., 998311"
                    />
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="fees_type">{t('masters.fees.feesType')} *</Label>
                  <Select
                    value={formData.fees_type}
                    onValueChange={(value: FeesType) => setFormData({ ...formData, fees_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('masters.fees.selectFeesType')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PERCENTAGE">{t('masters.fees.percentage')}</SelectItem>
                      <SelectItem value="FIXED">{t('masters.fees.fixed')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.fees_type === 'PERCENTAGE' ? (
                  <div className="grid gap-2">
                    <Label htmlFor="fees_percentage">{t('masters.fees.feesPercentage')}</Label>
                    <Input
                      id="fees_percentage"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.fees_percentage || ''}
                      onChange={(e) => setFormData({ ...formData, fees_percentage: parseFloat(e.target.value) || 0 })}
                      placeholder="e.g., 2.5"
                    />
                    <p className="text-sm text-muted-foreground">
                      {t('masters.fees.percentageApplied')}
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-2">
                    <Label htmlFor="fees_amount">{t('masters.fees.feesAmount')}</Label>
                    <Input
                      id="fees_amount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.fees_amount || ''}
                      onChange={(e) => setFormData({ ...formData, fees_amount: parseFloat(e.target.value) || 0 })}
                      placeholder="e.g., 500"
                    />
                    <p className="text-sm text-muted-foreground">
                      {t('masters.fees.fixedAmountAdded')}
                    </p>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {createMutation.isPending || updateMutation.isPending
                    ? t('common.saving')
                    : editingFees
                    ? t('masters.fees.updateFees')
                    : t('masters.fees.createFees')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center -mb-1">
        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10 pointer-events-none bg-white dark:bg-neutral-800 rounded-sm">
            <Search className="h-4 w-4 text-muted-foreground" />
          </div>
          <Input
            placeholder={t('masters.fees.searchPlaceholder')}
            value={filters.searchTerm}
            onChange={(e) => {
              setFilters({ ...filters, searchTerm: e.target.value })
              setCurrentPage(1)
            }}
            className="pl-9"
          />
        </div>

        <Select
          value={filters.type}
          onValueChange={(value) => {
            setFilters({ ...filters, type: value })
            setCurrentPage(1)
          }}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder={t('masters.fees.allTypes')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_TYPES}>{t('masters.fees.allTypes')}</SelectItem>
            <SelectItem value="PERCENTAGE">{t('masters.fees.percentage')}</SelectItem>
            <SelectItem value="FIXED">{t('masters.fees.fixed')}</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={filters.status}
          onValueChange={(value) => {
            setFilters({ ...filters, status: value })
            setCurrentPage(1)
          }}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder={t('common.allStatuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES}>{t('common.allStatuses')}</SelectItem>
            <SelectItem value="active">{t('common.active')}</SelectItem>
            <SelectItem value="inactive">{t('common.inactive')}</SelectItem>
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="mr-1 h-4 w-4" />
            {t('common.reset')}
          </Button>
        )}
      </div>

      <FeesTable
        feesList={paginatedFees}
        allFees={feesList}
        filteredFees={filteredFees}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={(id) => deleteMutation.mutate(id)}
        isDeleting={deleteMutation.isPending}
        clearFilters={clearFilters}
        pageSize={pageSize}
        currentPage={currentPage}
        totalRecords={totalRecords}
        totalPages={totalPages}
        startRecord={startRecord}
        endRecord={endRecord}
        onPageSizeChange={handlePageSizeChange}
        onNextPage={goToNextPage}
        onPreviousPage={goToPreviousPage}
        t={t}
        canAddEdit={canAddEdit('FEES_MASTER')}
      />
    </div>
  )
}

function FeesTable({
  feesList,
  allFees,
  filteredFees,
  isLoading,
  onEdit,
  onDelete,
  isDeleting,
  clearFilters,
  pageSize,
  currentPage,
  totalRecords,
  totalPages,
  startRecord,
  endRecord,
  onPageSizeChange,
  onNextPage,
  onPreviousPage,
  t,
  canAddEdit,
}: {
  feesList: FeesMasterListItem[]
  allFees: FeesMasterListItem[]
  filteredFees: FeesMasterListItem[]
  isLoading: boolean
  onEdit: (fees: FeesMasterListItem) => void
  onDelete: (id: string) => void
  isDeleting: boolean
  clearFilters: () => void
  pageSize: number
  currentPage: number
  totalRecords: number
  totalPages: number
  startRecord: number
  endRecord: number
  onPageSizeChange: (size: string) => void
  onNextPage: () => void
  onPreviousPage: () => void
  t: (key: string) => string
  canAddEdit: boolean
}) {
  if (isLoading) {
    return <div className="text-center py-8">{t('common.loading')}</div>
  }

  if (allFees.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          {t('masters.fees.noFeesFound')}
        </CardContent>
      </Card>
    )
  }

  if (filteredFees.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <div className="text-muted-foreground mb-4">
            {t('masters.fees.noMatchingFees')}
          </div>
          <Button variant="outline" onClick={clearFilters}>
            <X className="mr-2 h-4 w-4" />
            {t('common.reset')} {t('common.filters')}
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="flex flex-col h-[calc(100vh-280px)]">
      <Card className="flex-1 overflow-hidden flex flex-col">
        <div className="flex-1 overflow-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead>{t('masters.fees.feesName')}</TableHead>
                <TableHead>{t('masters.fees.feesCode')}</TableHead>
                <TableHead>{t('masters.fees.sacCode')}</TableHead>
                <TableHead>{t('masters.fees.feesType')}</TableHead>
                <TableHead>{t('masters.fees.rateAmount')}</TableHead>
                <TableHead>{t('common.status')}</TableHead>
                <TableHead className="w-[120px]">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {feesList.map((fees) => (
                <TableRow key={fees.fees_id}>
                  <TableCell className="font-medium">{fees.fees_name}</TableCell>
                  <TableCell>{fees.fees_code}</TableCell>
                  <TableCell>{fees.sac_code || '-'}</TableCell>
                  <TableCell>
                    {fees.fees_type === 'PERCENTAGE' ? (
                      <Badge variant="secondary">{t('masters.fees.percentage')}</Badge>
                    ) : (
                      <Badge variant="outline">{t('masters.fees.fixed')}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {fees.fees_type === 'PERCENTAGE' ? (
                      <span>{fees.fees_percentage}%</span>
                    ) : (
                      <span>₹{fees.fees_amount.toFixed(2)}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {fees.is_active ? (
                      <Badge variant="default">{t('common.active')}</Badge>
                    ) : (
                      <Badge variant="secondary">{t('common.inactive')}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {canAddEdit && (
                          <>
                            <DropdownMenuItem onClick={() => onEdit(fees)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              {t('common.edit')}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => onDelete(fees.fees_id)}
                              disabled={isDeleting}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              {t('common.delete')}
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {/* Pagination Controls */}
        <div className="flex items-center gap-3 px-4 py-2 border-t">
          <Select value={String(pageSize)} onValueChange={onPageSizeChange}>
            <SelectTrigger className="w-[70px] h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">
            {startRecord}-{endRecord} {t('common.of')} {totalRecords}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onPreviousPage}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onNextPage}
              disabled={currentPage >= totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}

